//#region types
import { AuthEnums } from "models/enums";
import {
  CostForwarding,
  CostForwardingPut,
  CostForwardingSource,
  ForwardableCost,
} from "models/utilTypes/costForwardingTypes";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";

// Raw routes until wint-api-client is regenerated from a Core build that has CostForwardingController.
const COST_FORWARDING_PATH = "api/CostForwarding";

const sourceSegments: Record<CostForwardingSource, string> = {
  receipt: "receipt",
  incomingInvoice: "incominginvoice",
};

const getSourcePath = (source: CostForwardingSource, sourceId: number) =>
  `${COST_FORWARDING_PATH}/${sourceSegments[source]}/${sourceId}`;

function useCostForwardingService() {
  const { apiClientInstance } = useAPIClientHelper();

  // No error toast: a document is opened for something else, and a failed read only hides the section.
  // The server answers 204 when the document is not marked, which arrives here as an empty body.
  const getMarking = (source: CostForwardingSource, sourceId: number): Promise<CostForwarding | undefined> =>
    apiClientInstance(AuthEnums.AuthType.Company)
      .get(getSourcePath(source, sourceId))
      .then((res) => (res.data ? (res.data as CostForwarding) : undefined));

  const putMarking = (source: CostForwardingSource, sourceId: number, marking: CostForwardingPut) =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .put(getSourcePath(source, sourceId), marking)
      .then((res) => (res.data ? (res.data as CostForwarding) : undefined));

  const releaseMarking = (costForwardingId: number) =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .post(`${COST_FORWARDING_PATH}/${costForwardingId}/release`)
      .then((res) => (res.data ? (res.data as CostForwarding) : undefined));

  // No error toast either: the list only drives the invoice form's reminder, and a failure must not interrupt writing
  // the invoice.
  const getForwardableCosts = (customerId: number): Promise<ForwardableCost[]> =>
    apiClientInstance(AuthEnums.AuthType.Company)
      .get(`${COST_FORWARDING_PATH}/forwardable`, { customerId })
      .then((res) => (res.data ?? []) as ForwardableCost[]);

  return { getMarking, putMarking, releaseMarking, getForwardableCosts };
}

export default useCostForwardingService;
